import { useAppStore } from "@/global/store";
import { selectBuckets } from "@/global/store/data/purchaseSeats/buckets";
import { selectEventsSelected } from "@/global/store/data/purchaseSeats/events";
import { selectSelectedPack } from "@/global/store/data/purchaseSeats/packs";
import {
  ModalActionButton,
  ModalActionButtonProps,
} from "@/shared/base/components/ModalActionButton/ModalActionButton";
import {
  calcMinMaxEventCountRequired,
  findBucketByEventCode,
} from "../../utils.events";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { selectPackageSelected } from "@/global/store/data/purchaseSeats/packages";
import { Link, Typography } from "@mui/material";
import { isPackBonusEvent } from "../../Packs/packs.utils";

export function ContinueWithoutSelectionButton(props: {
  eventCode: string;
  onClickContinue: () => void;
  onClickBackToFindGames: () => void;
  buttonProps?: ModalActionButtonProps;
}) {
  const teamConfig = useTeamConfig();
  const selectedEvents = useAppStore(selectEventsSelected);
  const selectedPack = useAppStore(selectSelectedPack);
  const selectedPackage = useAppStore(selectPackageSelected);
  const buckets = useAppStore(selectBuckets);
  const selectedBucket = findBucketByEventCode(
    selectedPack,
    buckets,
    teamConfig,
    props?.eventCode
  );

  const updatedSelectedEvents = selectedEvents.filter((evt) => {
    return evt.eventCode !== props?.eventCode;
  });

  const { minEventCountRequired } = calcMinMaxEventCountRequired(
    teamConfig,
    selectedBucket,
    selectedPackage
  );

  // returning secondary button for buckets
  if (teamConfig?.packs?.enabled || teamConfig?.buckets?.enabled) {
    const selectedBucketEvents = selectedEvents.filter((evt) => {
      const isBonusEvent = isPackBonusEvent(evt, selectedPack);
      return (
        evt?.eventCode !== props?.eventCode &&
        evt.bucketId === selectedBucket?.id &&
        !isBonusEvent
      );
    });

    return (
      <>
        {updatedSelectedEvents?.length &&
        selectedBucketEvents?.length >= minEventCountRequired ? (
          <ModalActionButton
            variant="outlined"
            onClick={props.onClickContinue}
            {...props?.buttonProps}
          >
            Remove Event
          </ModalActionButton>
        ) : null}
      </>
    );
  }

  if (teamConfig?.packageConfigs?.isPackageEnabled) {
    const selectedPackageEvents = selectedEvents.filter((evt) => {
      return evt.eventCode !== props?.eventCode;
    });

    return (
      <>
        {selectedPackageEvents.length > 0 &&
        selectedPackageEvents.length >= minEventCountRequired ? (
          <ModalActionButton
            variant="outlined"
            onClick={props.onClickContinue}
            {...props?.buttonProps}
          >
            Remove Event
          </ModalActionButton>
        ) : null}
      </>
    );
  }

  return (
    <>
      {updatedSelectedEvents.length &&
      updatedSelectedEvents.length >= minEventCountRequired ? (
        <ModalActionButton
          variant="outlined"
          onClick={props.onClickContinue}
          {...props?.buttonProps}
        >
          Remove Event
        </ModalActionButton>
      ) : null}
    </>
  );
}

export function NoInventoryModalMessage(props: {
  eventCode: string;
  onClickAdjustFilters?: () => void;
  isInventoryApiTriggered?: boolean;
}) {
  const teamConfig = useTeamConfig();
  const selectedEvents = useAppStore(selectEventsSelected);
  const selectedPack = useAppStore(selectSelectedPack);
  const selectedPackage = useAppStore(selectPackageSelected);
  const buckets = useAppStore(selectBuckets);
  const selectedBucket = findBucketByEventCode(
    selectedPack,
    buckets,
    teamConfig,
    props?.eventCode
  );

  const updatedSelectedEvents = selectedEvents.filter((evt) => {
    return evt.eventCode !== props?.eventCode;
  });

  const { minEventCountRequired } = calcMinMaxEventCountRequired(
    teamConfig,
    selectedBucket,
    selectedPackage
  );

  if (props?.isInventoryApiTriggered) {
    return (
      teamConfig?.languages?.noInventoryAlertModal?.description
        ?.inventoryApiTriggered || (
        <Typography variant="body1">
          It looks like this event is running low on inventory. Please try
          adjusting the{" "}
          <Link
            className="global-link"
            fontWeight={"500"}
            onClick={props?.onClickAdjustFilters}
          >
            filters.
          </Link>
        </Typography>
      )
    );
  }

  const minRestrictionOnLang = teamConfig?.languages?.noInventoryAlertModal
    ?.description?.minRestrictionOn || (
    <Typography variant="body1">
      It looks like this event is running low on inventory. Please select
      another seat or try a different event.
    </Typography>
  );

  const minRestrictionOffLang = teamConfig?.languages?.noInventoryAlertModal
    ?.description?.minRestrictionOff || (
    <Typography variant="body1">
      It looks like this event is running low on inventory. Please select
      another seat, try a different event or remove this event from your order.
    </Typography>
  );

  if (teamConfig?.packs?.enabled || teamConfig?.buckets?.enabled) {
    const selectedBucketEvents = selectedEvents.filter((evt) => {
      return (
        evt?.eventCode !== props?.eventCode &&
        evt.bucketId === selectedBucket?.id
      );
    });

    return (
      <>
        {updatedSelectedEvents?.length &&
        selectedBucketEvents?.length >= minEventCountRequired ? (
          <>{minRestrictionOffLang}</>
        ) : (
          <>{minRestrictionOnLang}</>
        )}
      </>
    );
  }

  if (teamConfig?.packageConfigs?.isPackageEnabled) {
    const selectedPackageEvents = selectedEvents.filter((evt) => {
      return evt.eventCode !== props?.eventCode;
    });

    return (
      <>
        {selectedPackageEvents.length > 0 &&
        selectedPackageEvents.length >= minEventCountRequired ? (
          <>{minRestrictionOffLang}</>
        ) : (
          <>{minRestrictionOnLang}</>
        )}
      </>
    );
  }

  return (
    <>
      {updatedSelectedEvents.length &&
      updatedSelectedEvents.length >= minEventCountRequired ? (
        <>{minRestrictionOffLang}</>
      ) : (
        <>{minRestrictionOnLang}</>
      )}
    </>
  );
}
